import type { AdvisorAccount } from "@/utils/accounting";
import { formatCOP, formatNumber } from "@/utils/format";

interface AdvisorAccountCardsProps {
  accounts: AdvisorAccount[];
  selectedId: string;
  onSelect: (id: string) => void;
  onDeliver: (account: AdvisorAccount) => void;
  onPay: (account: AdvisorAccount) => void;
  onExport: (account: AdvisorAccount) => void;
}

// Ficha por asesor: qué compró, qué tiene en consignación y cuánto debe.
const AdvisorAccountCards = ({
  accounts,
  selectedId,
  onSelect,
  onDeliver,
  onPay,
  onExport,
}: AdvisorAccountCardsProps) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
    {accounts.map((a) => (
      <div
        key={a.advisor.id}
        onClick={() => onSelect(a.advisor.id)}
        className={`glass-panel rounded-lg p-4 cursor-pointer transition-colors animate-fade-up ${
          selectedId === a.advisor.id ? "border-accent-500/55 ring-2 ring-accent-500/20" : "hover:border-accent-500/35"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground-950 truncate">
              {a.advisor.names} {a.advisor.surnames}
            </p>
            <p className="text-[11px] text-foreground-500">Margen {a.margin}%</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-accent-500/15 text-accent-200">
              {a.advisor.city || "Sin ciudad"}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExport(a);
              }}
              className="w-7 h-7 flex items-center justify-center rounded-md border border-accent-500/30 text-accent-200 hover:bg-accent-500/10 cursor-pointer"
              aria-label="Exportar cuenta a Excel"
              title="Exportar cuenta a Excel"
            >
              <i className="ri-file-excel-2-line text-sm" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
          <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2 py-1.5">
            <p className="text-foreground-500">Compró</p>
            <p className="font-semibold text-foreground-800">{formatCOP(a.compradoValue)}</p>
            <p className="text-foreground-500">{formatNumber(a.compradoUnits)} und</p>
          </div>
          <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2 py-1.5">
            <p className="text-foreground-500">Tenencia</p>
            <p className="font-semibold text-secondary-200">{formatCOP(a.consignedValue)}</p>
            <p className="text-foreground-500">{formatNumber(a.consignedUnits)} und consignadas</p>
          </div>
          <div className="rounded-md bg-background-200/40 border border-background-300/40 px-2 py-1.5">
            <p className="text-foreground-500">Deuda</p>
            <p className={`font-semibold ${a.debt > 0 ? "text-accent-200" : "text-foreground-700"}`}>
              {formatCOP(a.debt)}
            </p>
            <p className="text-foreground-500">por ventas</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeliver(a);
            }}
            className="flex-1 px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
          >
            <i className="ri-add-line" /> Entregar / Vender
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPay(a);
            }}
            disabled={a.debt <= 0}
            className={`flex-1 px-3 py-2 rounded-md text-[11px] font-semibold whitespace-nowrap inline-flex items-center justify-center gap-1.5 ${
              a.debt <= 0
                ? "text-foreground-500/40 cursor-not-allowed"
                : "border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 cursor-pointer"
            }`}
          >
            <i className="ri-hand-coin-line" /> Cobrar
          </button>
        </div>

        <p className="text-[9px] text-foreground-500 mt-2.5 leading-relaxed">
          <span className="font-semibold text-foreground-700">Tenencia</span> = mercancía en
          custodia (no es deuda).{" "}
          <span className="font-semibold text-foreground-700">Deuda</span> = solo lo vendido;
          devolver baja la tenencia.
        </p>
      </div>
    ))}
  </div>
);

export default AdvisorAccountCards;